import React, { useState, useEffect } from "react";
import { addDoc, updateDoc, doc, collection } from "firebase/firestore";
import { db } from "../../../firebaseConfig"; // Adjust path as necessary

function IssueForm({ volumeId, issue, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [order, setOrder] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (issue) {
      setTitle(issue.title);
      setDate(issue.date);
      setOrder(issue.order);
    } else {
      // For new issues, set default values
      setTitle("");
      setDate(""); // e.g., "January 2023"
      setOrder("");
    }
  }, [issue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!volumeId) {
      setError("No volume selected. Cannot save issue.");
      setLoading(false);
      return;
    }

    try {
      const issueData = {
        title,
        date,
        order: parseInt(order),
        articles: issue ? issue.articles : [], // Preserve existing articles or initialize empty
        isLatest: issue ? issue.isLatest || false : false,
      };

      if (issue) {
        // Update existing issue
        await updateDoc(
          doc(db, `journalVolumes/${volumeId}/issues`, issue.id),
          issueData,
        );
      } else {
        // Add new issue
        await addDoc(
          collection(db, `journalVolumes/${volumeId}/issues`),
          issueData,
        );
      }
      onSave(); // Refresh parent list
      onClose(); // Close modal
    } catch (err) {
      console.error("Error saving issue:", err);
      setError("Failed to save issue. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <h3 className="text-lg font-bold mb-4 text-slate-800">
          {issue ? "Edit Issue" : "Add New Issue"}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="issueTitle"
              className="block text-sm font-medium text-gray-700"
            >
              Title (e.g., Issue 1)
            </label>
            <input
              type="text"
              id="issueTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-sky-500 focus:border-sky-500"
              required
            />
          </div>
          <div>
            <label
              htmlFor="issueDate"
              className="block text-sm font-medium text-gray-700"
            >
              Date (e.g., January 2023)
            </label>
            <input
              type="text"
              id="issueDate"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-sky-500 focus:border-sky-500"
              required
            />
          </div>
          <div>
            <label
              htmlFor="issueOrder"
              className="block text-sm font-medium text-gray-700"
            >
              Order (for sorting)
            </label>
            <input
              type="number"
              id="issueOrder"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-sky-500 focus:border-sky-500"
              required
            />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Issue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IssueForm;
